import React, { useEffect, useState } from 'react';

function linesToArray(text) {
  return String(text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

function arrayToLines(arr) {
  return (Array.isArray(arr) ? arr : []).join('\n');
}

function guestLabel(guest) {
  if (!guest) return '';
  const name = guest.name || 'Guest';
  const company = guest.company || guest.title || '';
  const email = guest.email || '';
  return `${name}${company ? ` — ${company}` : ''}${email ? ` (${email})` : ''}`;
}

function initialSelectedGuests(draft) {
  if (Array.isArray(draft?.guest_ids) && draft.guest_ids.length > 0) {
    const guestsById = new Map(
      (Array.isArray(draft.guests) ? draft.guests : [])
        .filter((g) => g && (g.guest_id || g.id))
        .map((g) => [g.guest_id || g.id, g])
    );
    return draft.guest_ids.map((id) => {
      const g = guestsById.get(id);
      return {
        id,
        label: g ? guestLabel(g) : id,
        guest: g || { guest_id: id },
      };
    });
  }
  if (draft?.guest_id || draft?.guest?.guest_id || draft?.guest?.name) {
    const id = draft.guest_id || draft.guest?.guest_id || '';
    return [
      {
        id: id || `name:${draft.guest?.name || 'guest'}`,
        label: guestLabel(draft.guest) || String(id),
        guest: draft.guest || { guest_id: id },
      },
    ];
  }
  return [];
}

export default function EpisodeDraftReview({ draft, onDraftUpdated, onPublished }) {
  const [title, setTitle] = useState(draft?.title || '');
  const [summary, setSummary] = useState(draft?.summary || '');
  const [showNotesText, setShowNotesText] = useState(arrayToLines(draft?.show_notes));
  const [takeawaysText, setTakeawaysText] = useState(arrayToLines(draft?.key_takeaways));
  const [categoriesText, setCategoriesText] = useState(arrayToLines(draft?.categories));
  const [tagsText, setTagsText] = useState(arrayToLines(draft?.tags));
  const [slug, setSlug] = useState(draft?.slug || '');
  const [youtubeId, setYoutubeId] = useState(draft?.youtube_id || '');
  const [spotifyUrl, setSpotifyUrl] = useState(draft?.spotify_embed_url || '');
  const [duration, setDuration] = useState(draft?.duration || '');
  const [approvalToken, setApprovalToken] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [guestSearch, setGuestSearch] = useState('');
  const [guestResults, setGuestResults] = useState([]);
  const [selectedGuests, setSelectedGuests] = useState(() => initialSelectedGuests(draft));
  const [guestSearchStatus, setGuestSearchStatus] = useState('idle');

  useEffect(() => {
    setSelectedGuests(initialSelectedGuests(draft));
  }, [draft?.draft_id, draft?.guest_id, Array.isArray(draft?.guest_ids) ? draft.guest_ids.join(',') : '']);

  useEffect(() => {
    const q = guestSearch.trim();
    if (q.length < 2) {
      setGuestResults([]);
      setGuestSearchStatus('idle');
      return undefined;
    }

    setGuestSearchStatus('searching');
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/ao/auto/guest-search?q=${encodeURIComponent(q)}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.ok) throw new Error(json.error || 'Search failed');
        setGuestResults(json.guests || []);
        setGuestSearchStatus('idle');
      } catch {
        setGuestResults([]);
        setGuestSearchStatus('error');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [guestSearch]);

  if (!draft?.draft_id) return null;

  const selectedIds = selectedGuests.map((g) => g.id).filter((id) => id && !String(id).startsWith('name:'));
  const primaryGuest = selectedGuests[0]?.guest || null;
  const primaryGuestId = selectedIds[0] || null;

  const addGuestSelection = (guest) => {
    if (!guest?.id) return;
    setSelectedGuests((prev) => {
      if (prev.some((g) => g.id === guest.id)) return prev;
      return [
        ...prev,
        {
          id: guest.id,
          label: guestLabel(guest),
          guest: {
            guest_id: guest.id,
            name: guest.name,
            company: guest.company || '',
            bio: guest.bio_md || guest.bio || '',
            image: guest.image_url || guest.image || '',
            website: guest.website || '',
            social_links: guest.social_links || [],
            email: guest.email || '',
          },
        },
      ];
    });
    setGuestSearch('');
    setGuestResults([]);
  };

  const removeGuestSelection = (id) => {
    setSelectedGuests((prev) => prev.filter((g) => g.id !== id));
  };

  const saveDraft = async () => {
    setStatus('saving');
    setMessage('');
    try {
      const multi = selectedIds.length > 1;
      const res = await fetch(`/api/ao/auto/episode-drafts/${draft.draft_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          summary,
          show_notes: linesToArray(showNotesText),
          key_takeaways: linesToArray(takeawaysText),
          categories: linesToArray(categoriesText),
          tags: linesToArray(tagsText),
          slug: slug.trim() || undefined,
          youtube_id: youtubeId.trim(),
          spotify_embed_url: spotifyUrl.trim(),
          duration: duration.trim(),
          guest_id: primaryGuestId,
          guest: primaryGuest,
          guest_ids: selectedIds.length > 0 ? selectedIds : null,
          guests: multi ? selectedGuests.map((g) => g.guest).filter(Boolean) : null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Could not save draft');
      onDraftUpdated?.(json.draft);
      setStatus('saved');
      setMessage('Draft saved.');
      return true;
    } catch (e) {
      setStatus('error');
      setMessage(e.message || 'Save failed');
      return false;
    }
  };

  const mintApproval = async () => {
    const slugValue = slug.trim();
    if (!slugValue) {
      setStatus('error');
      setMessage('Enter a slug before approving.');
      return;
    }
    setStatus('approving');
    setMessage('');
    const saved = await saveDraft();
    if (!saved) return;
    try {
      const res = await fetch('/api/ao/journal/publish-approval', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: slugValue, kind: 'episode' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Could not mint approval token');
      setApprovalToken(json.publish_approval_token || '');
      setStatus('approved');
      setMessage('Approval token ready. Click Publish episode.');
    } catch (e) {
      setStatus('error');
      setMessage(e.message || 'Approval failed');
    }
  };

  const publishEpisode = async () => {
    const slugValue = slug.trim();
    if (!slugValue || !approvalToken) {
      setStatus('error');
      setMessage('Slug and approval token are required.');
      return;
    }
    setStatus('publishing');
    setMessage('');
    try {
      const res = await fetch('/api/ao/auto/episode-publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_id: draft.draft_id,
          slug: slugValue,
          youtube_id: youtubeId.trim(),
          spotify_embed_url: spotifyUrl.trim(),
          duration: duration.trim(),
          publish_approval_token: approvalToken,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Publish failed');
      setLiveUrl(json.podcast_url || '');
      if (json.youtube_id) setYoutubeId(json.youtube_id);
      setStatus('published');
      setMessage(json.message || 'Episode published.');
      onPublished?.(json);
    } catch (e) {
      setStatus('error');
      setMessage(e.message || 'Publish failed');
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-4 overflow-y-auto">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Podcast episode draft</p>

      <label className="block text-xs font-medium text-gray-600">
        Title
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
      </label>

      <label className="block text-xs font-medium text-gray-600">
        Summary (callout)
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={4}
          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
      </label>

      <label className="block text-xs font-medium text-gray-600">
        Show notes (one per line)
        <textarea
          value={showNotesText}
          onChange={(e) => setShowNotesText(e.target.value)}
          rows={4}
          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
        />
      </label>

      <label className="block text-xs font-medium text-gray-600">
        Key takeaways (one per line)
        <textarea
          value={takeawaysText}
          onChange={(e) => setTakeawaysText(e.target.value)}
          rows={3}
          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
        />
      </label>

      <label className="block text-xs font-medium text-gray-600">
        Categories (one per line)
        <textarea
          value={categoriesText}
          onChange={(e) => setCategoriesText(e.target.value)}
          rows={2}
          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
        />
      </label>

      <label className="block text-xs font-medium text-gray-600">
        Tags (one per line)
        <textarea
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          rows={2}
          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
        />
      </label>

      <div className="border border-gray-200 rounded-lg p-3 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Guests from intake</p>
        <p className="text-[11px] text-gray-500 leading-snug">
          Search by name or email to attach one or more submitted guest intake records to this episode.
        </p>
        <input
          type="text"
          value={guestSearch}
          onChange={(e) => setGuestSearch(e.target.value)}
          placeholder="Search guest name or email"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        {guestSearchStatus === 'searching' && (
          <p className="text-xs text-gray-500">Searching...</p>
        )}
        {guestResults.length > 0 && (
          <ul className="max-h-40 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
            {guestResults.map((guest) => {
              const already = selectedGuests.some((g) => g.id === guest.id);
              return (
                <li key={guest.id}>
                  <button
                    type="button"
                    disabled={already}
                    onClick={() => addGuestSelection(guest)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="font-medium text-gray-900">{guest.name}</span>
                    {guest.company && <span className="text-gray-500"> — {guest.company}</span>}
                    <span className="block text-xs text-gray-500">
                      {guest.email}
                      {already ? ' · already attached' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {selectedGuests.length > 0 && (
          <ul className="flex flex-wrap gap-2 pt-1">
            {selectedGuests.map((g) => (
              <li
                key={g.id}
                className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800"
              >
                <span>{g.label}</span>
                <button
                  type="button"
                  onClick={() => removeGuestSelection(g.id)}
                  className="text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-red-600"
                  aria-label={`Remove ${g.label}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-gray-200 pt-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Publish details</p>
        <p className="text-[11px] text-gray-500 leading-snug">
          Riverside handles YouTube, Spotify, and Apple distribution. Paste platform IDs and links here after
          Riverside publishes. Auto publishes the episode page on the site only.
        </p>
        <label className="block text-xs font-medium text-gray-600">
          Slug
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="episode-slug"
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-xs font-medium text-gray-600">
          YouTube video ID (from Riverside / YouTube URL)
          <input
            type="text"
            value={youtubeId}
            onChange={(e) => setYoutubeId(e.target.value)}
            placeholder="dQw4w9WgXcQ"
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-xs font-medium text-gray-600">
          Spotify embed URL
          <input
            type="text"
            value={spotifyUrl}
            onChange={(e) => setSpotifyUrl(e.target.value)}
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-xs font-medium text-gray-600">
          Duration
          <input
            type="text"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="42 min"
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </label>
      </div>

      {(Array.isArray(draft.corpus_connections) && draft.corpus_connections.length > 0) ||
      (Array.isArray(draft.thematic_threads) && draft.thematic_threads.length > 0) ? (
        <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Corpus intelligence</p>

          {Array.isArray(draft.corpus_connections) && draft.corpus_connections.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-gray-900">Corpus connections (all strengths)</p>
              <ul className="space-y-3 text-sm text-gray-700">
                {draft.corpus_connections.map((conn, i) => (
                  <li key={`conn-${i}`} className="rounded border border-gray-200 bg-white p-3">
                    <p className="font-medium text-gray-900">
                      {conn.title}{' '}
                      <span className="text-xs font-normal text-gray-500">({conn.strength || 'thematic'})</span>
                    </p>
                    {conn.connection && <p className="mt-1">{conn.connection}</p>}
                    {conn.timestamp && (
                      <p className="mt-1 text-xs text-gray-500">Timestamp: {conn.timestamp}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(draft.thematic_threads) && draft.thematic_threads.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-gray-900">Thematic threads</p>
              <ul className="space-y-3 text-sm text-gray-700">
                {draft.thematic_threads.map((thread, i) => (
                  <li key={`thread-${i}`} className="rounded border border-gray-200 bg-white p-3">
                    <p className="font-medium text-gray-900">{thread.thread}</p>
                    {thread.description && <p className="mt-1">{thread.description}</p>}
                    {thread.suggested_follow_up && (
                      <p className="mt-2 text-xs text-gray-600">Follow-up: {thread.suggested_follow_up}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      {message && (
        <p
          className={`text-sm ${status === 'error' ? 'text-red-600' : status === 'published' ? 'text-green-700' : 'text-gray-600'}`}
        >
          {message}
        </p>
      )}

      {liveUrl && (
        <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline break-all">
          {liveUrl}
        </a>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          type="button"
          onClick={saveDraft}
          disabled={status === 'saving' || status === 'publishing'}
          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
        >
          Save edits
        </button>
        <button
          type="button"
          onClick={mintApproval}
          disabled={status === 'publishing'}
          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={publishEpisode}
          disabled={!approvalToken || status === 'publishing'}
          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
        >
          Publish episode
        </button>
      </div>
    </div>
  );
}
